const SVG_NS = 'http://www.w3.org/2000/svg'

function findLegendRoot(svgNode) {
  const namedLegend = svgNode.querySelector('#legend')
  if (namedLegend) return namedLegend

  return Array.from(svgNode.querySelectorAll('g')).find((node) => {
    const id = node.getAttribute('id') || ''
    const className = node.getAttribute('class') || ''
    const dataName = node.getAttribute('data-name') || ''
    return /legend/i.test(id) || /legend/i.test(className) || /legend/i.test(dataName)
  }) || null
}

function chartSize(svgNode) {
  const viewBox = (svgNode.getAttribute('viewBox') || '').trim().split(/[ ,]+/).map(Number)
  if (viewBox.length === 4 && viewBox.every(Number.isFinite)) {
    return { width: viewBox[2], height: viewBox[3] }
  }
  return {
    width: Number.parseFloat(svgNode.getAttribute('width')) || 0,
    height: Number.parseFloat(svgNode.getAttribute('height')) || 0,
  }
}

function getLayoutWrapper(legendRoot) {
  const parent = legendRoot.parentNode
  if (parent?.getAttribute?.('data-lugsail-legend-layout') === 'true') return parent

  const wrapper = document.createElementNS(SVG_NS, 'g')
  wrapper.setAttribute('data-lugsail-legend-layout', 'true')
  legendRoot.parentNode.insertBefore(wrapper, legendRoot)
  wrapper.appendChild(legendRoot)
  return wrapper
}

function getTranslate(transform = '') {
  const match = transform.match(/translate\s*\(\s*([^,\s)]+)(?:[,\s]+([^\s)]+))?\s*\)/)
  return match ? { x: Number(match[1]) || 0, y: Number(match[2]) || 0 } : { x: 0, y: 0 }
}

// D3 legends are built from independently positioned `.cell` groups. Scaling
// those cells, rather than only their outer container, makes the text and
// swatches visibly larger and keeps their spacing proportional.
function scaleLegendCells(legendRoot, scale) {
  const cells = Array.from(legendRoot.querySelectorAll('.cell'))
  if (!cells.length) return false

  cells.forEach((cell) => {
    if (cell.dataset.lugsailLegendBaseTransform === undefined) {
      cell.dataset.lugsailLegendBaseTransform = cell.getAttribute('transform') || ''
    }
    const baseTransform = cell.dataset.lugsailLegendBaseTransform
    const { x, y } = getTranslate(baseTransform)
    cell.setAttribute('transform', `translate(${x * scale},${y * scale}) scale(${scale})`)
  })
  return true
}

/**
 * Place the complete legend group inside the existing SVG artboard. The
 * underlying RAWGraphs legend remains untouched, so its cells and swatches
 * preserve their chart-specific rendering and export grouping.
 */
export function applyLegendPlacement(svgNode, theme = {}) {
  const placement = theme.legendPlacement || 'auto'
  const scale = Number.isFinite(Number(theme.legendScale))
    ? Math.max(0.7, Math.min(1.4, Number(theme.legendScale)))
    : 1
  if (!svgNode) return false

  const legendRoot = findLegendRoot(svgNode)
  if (!legendRoot || typeof legendRoot.getBBox !== 'function') return false
  const existingWrapper = legendRoot.parentNode?.getAttribute?.('data-lugsail-legend-layout') === 'true'
    ? legendRoot.parentNode
    : null
  if (placement === 'auto' && scale === 1) {
    scaleLegendCells(legendRoot, 1)
    if (!existingWrapper) return false
    existingWrapper.removeAttribute('transform')
    return true
  }
  const { width, height } = chartSize(svgNode)
  if (!(width > 0 && height > 0)) return false

  const cellsScaled = scaleLegendCells(legendRoot, scale)
  let bbox
  try {
    bbox = legendRoot.getBBox()
  } catch {
    return false
  }
  if (!Number.isFinite(bbox.width) || !Number.isFinite(bbox.height)) return false

  const inset = 16
  // getBBox sees the already-scaled cells. Charts without cell groups fall
  // back to scaling the outer group below.
  const contentScale = cellsScaled ? 1 : scale
  const scaledWidth = bbox.width * contentScale
  const scaledHeight = bbox.height * contentScale
  let left = bbox.x * contentScale
  let top = bbox.y * contentScale

  if (placement === 'top' || placement === 'bottom') {
    left = Math.max(inset, (width - scaledWidth) / 2)
    top = placement === 'top'
      ? inset
      : Math.max(inset, height - scaledHeight - inset)
  } else if (placement === 'left' || placement === 'right') {
    left = placement === 'left'
      ? inset
      : Math.max(inset, width - scaledWidth - inset)
    top = Math.max(inset, (height - scaledHeight) / 2)
  }

  const wrapper = getLayoutWrapper(legendRoot)
  wrapper.setAttribute(
    'transform',
    `translate(${left - bbox.x * contentScale},${top - bbox.y * contentScale}) scale(${contentScale})`
  )
  return true
}
